import { useAccount } from "@qw/auth";
import { useInboxAgencySearchQuery, useInboxAgentSearchQuery, useInboxSearchSuggestionsQuery } from "@qw/graphql";
import { Command, CommandInput, CommandItem, CommandSeparator, Icon } from "@qw/ui";
import {
  capture,
  PostHogEvent,
  SearchFilterAction,
  SearchFilterSurface,
} from "@third-plane/posthog-integration-interface";
import { type FC, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { useDebounceValue } from "usehooks-ts";
import { useSearchInsureds } from "@/book/use-search-insureds";
import { useInboxFilter } from "@/providers/inbox-filter-provider";
import { useInboxQueryStates } from "@/providers/inbox-query-states-provider";
import { type InboxScope, scopeSegment } from "@/providers/inbox-scope";
import { useBook } from "@/providers/use-book";
import { useMailHorizonDescription } from "@/providers/use-mail-horizon";
import { usePage } from "@/providers/use-page";
import { parseSearchQuery, type SearchOperator, searchTextLength } from "@/search/search-operators";
import {
  AgencySuggestion,
  AgentSuggestion,
  ConversationSuggestion,
  InsuredSuggestion,
  OperatorOffer,
  RecentSearchItem,
  SearchDropdown,
  SearchOfferRow,
  UnlinkedScopeOffer,
} from "./qwib-search-rows";
import { SearchScopeChips, useClearScopeTarget } from "./search-scope-chips";
import { useRecentSearches } from "./use-recent-searches";

// Conversation search is a server-side scan, so it waits for the typing to settle. The insured half
// stays on the live term — `useSearchInsureds` fuzzy-matches the loaded book locally and applies its
// own debounce to the one query it makes.
const SUGGESTION_DEBOUNCE_MS = 200;
const MINIMUM_SUGGESTION_TERM_LENGTH = 3;

/**
 * Both conversation queries ask for the same window, which is wider than the bands below render.
 * That shared limit is what makes the two results comparable: the header search returns a strict
 * subset of the full one in the same server order, so inside an identical limit every header hit
 * the full search can return is also present in the header result. Ask for a narrower header
 * window and a subject match ranked below it would be missing from the header set, and the body
 * band — which is defined by absence from that set — would render it as a body match.
 */
const CONVERSATION_QUERY_LIMIT = 12;

/**
 * One list, six bands, fixed slots.
 *
 * The four sources score on scales that do not compare — microfuzz distance for insureds, a name
 * match for agents, match quality then book size for agencies, the server's match rank for
 * conversations — so the order is a fixed sequence of bands rather than a single sort key, and each
 * band holds its own slots. Without them a term matching forty insureds would push every email out
 * of a list whose whole promise is finding both.
 *
 * Body-only conversations sit last for two reasons that happen to agree. They are the weakest
 * signal the server has — rank 4 means "the term appeared somewhere inside an email" — and they are
 * the half that arrives late. Last place means the late half appends below every row the broker is
 * already reading instead of pushing it down. The slots are deliberately not backfilled from bands
 * that came back empty, for that same reason: a band that grew when another resolved would move
 * rows out from under the pointer.
 */
const MY_INSURED_SLOTS = 3;
/**
 * Two, because the terms brokers type are the ambiguous ones. Inside a single broker's own agents a
 * full name is unique 94% of the time, but first-name-only collides 38.9% of the time and
 * last-name-only 22.7% — and someone who types "sarah" should not be handed one Sarah and left to
 * guess whether she is the right one. Both slots filling is expected here rather than a defect.
 */
const AGENT_SLOTS = 2;
/**
 * Two, for the reason {@link AGENT_SLOTS} is two: the term a broker types is a fragment, and the
 * fragment is the ambiguous part. Agency names cluster hard on shared words — 67% of them contain
 * "Insurance" — so a few characters reach several agencies more often than one, and a single row
 * hands the broker one of them with nothing to compare it against.
 *
 * Two also makes the counts on the rows legible. They are there to answer "is scoping worth the
 * click", and a number reads as large or small against the other agency in front of it.
 */
const AGENCY_SLOTS = 2;
const HEADER_CONVERSATION_SLOTS = 4;
const OTHER_INSURED_SLOTS = 2;
const BODY_CONVERSATION_SLOTS = 3;

/**
 * A query in the fast band counts as answered when it has data *or* an error.
 *
 * A failed name search reads as "no agents" or "no agencies" — the direction `inbox-filter-provider`
 * already takes for its committed-search probe — because the alternative is one failing query
 * holding the email half of the dropdown back for good.
 */
const hasAnswered = (data: unknown, error: unknown) => Boolean(data) || Boolean(error);

// The box renders above every section, but the sections cannot honour it the same way. Only the
// inbox renders a conversation column and a mail list, so only the inbox can show a committed
// search — drafts has a book but nothing beside it to fill, and standalone chat has neither. The
// layout knows which route it is on, so it says which, rather than the box re-deriving it.
export type QwibSearchSection = "inbox" | "drafts" | "standalone";

export const QwibSearch: FC<{ section?: QwibSearchSection }> = ({ section = "inbox" }) => {
  // Non-null exactly when the route is scoping the box — to an insured, an agent or an agency — so
  // it doubles as "is there a chip".
  const clearScopeTarget = useClearScopeTarget();
  const { pathname, search: locationSearch } = useLocation();
  const navigate = useNavigate();
  const { userInbox } = useAccount();
  const { filters } = useInboxFilter();
  const { setIsBookViewExpanded, showExpired } = useBook();
  const { rootPath, rootScopePath } = usePage();
  const {
    searchTerm,
    setConversationId,
    subjectSearch,
    setSubjectSearch,
    senderSearch,
    setSenderSearch,
    clearSearch,
    unlinked,
    setUnlinked,
  } = useInboxQueryStates();
  // Recorded from every section, offered back only where the dropdown renders them (see below), so
  // a broker who searched from drafts still finds that term waiting in the inbox.
  const { recentSearches, recordRecentSearch, removeRecentSearch } = useRecentSearches();

  // Picking a row stays where the broker is: drafts roots its own book at `/drafts` (see
  // `left-sidebar.tsx`) while the singleton `PageProvider` stays on `/inbox`, and ejecting them to
  // the inbox for choosing an insured is what an earlier review caught.
  const insuredRootPath = section === "drafts" ? "/drafts" : rootPath;
  // The inbox is the only section rendering a conversation column, so an email pick from anywhere
  // else has to travel — to `rootScopePath` rather than the bare root, because on
  // `/drafts/:insuredId` the pick is a move inside that insured's mail and landing on `/inbox`
  // would drop the scope the broker is working in. `folder-nav.tsx` carries the scope across a
  // folder change the same way — Inbox aside, which is the row that exists to leave the scope.
  // Standalone chat has no scope in its path, so there it is just `rootPath`.
  const ownsSearchSurfaces = section === "inbox";

  const inputRef = useRef<HTMLInputElement>(null);
  // The box and its dropdown together, which is the subtree `handleBlur` asks about.
  const containerRef = useRef<HTMLDivElement>(null);
  // The typed term is deliberately not the committed `?term=`: the dropdown reacts to every
  // keystroke, while the mail list and the book only move once the broker commits with Enter.
  const [term, setTerm] = useState(searchTerm);
  const [isOpen, setIsOpen] = useState(false);
  const [settledTerm] = useDebounceValue(term, SUGGESTION_DEBOUNCE_MS);
  // The term the broker typed and when they started typing it, so the settled term can be checked
  // against what was actually typed rather than merely being non-empty.
  const typedSearchRef = useRef<{ term: string; startedAt: number } | null>(null);
  // cmdk always highlights its first item, so a bare Enter would open an insured the broker never
  // asked for. Enter only reaches the highlight once they have moved it themselves.
  const hasMovedHighlightRef = useRef(false);

  // Keeps the box honest when `?term=` moves without us — a back/forward navigation, or another
  // surface clearing the search.
  useEffect(() => {
    setTerm(searchTerm);
  }, [searchTerm]);

  // Only a term the broker typed is a search. `?term=` also moves on back/forward and on a commit,
  // so the start time is consumed here to keep those from emitting a duplicate with a stale duration.
  //
  // The stamp belongs to the term it was typed for, which is why both are kept together. Holding
  // only a start time was not enough: every keystroke stamps one, including the keystroke that
  // empties the box, and settling from empty back to empty is not a change — so the effect never
  // ran to spend it. A broker who typed and backed out left a live stamp behind, and the next term
  // to arrive on its own would spend it on a search nobody typed. Picking a recent row is exactly
  // that: it commits through `?term=`, which returns as a term the box never typed. Comparing the
  // terms makes every non-typed arrival — URL sync, a cleared box, a dismissed dropdown, a recent
  // row — fail to match, without a clearing call at each of those sites.
  useEffect(() => {
    const typedSearch = typedSearchRef.current;

    // The unscoped half rather than the whole parsed term, because `insureds` names a band and an
    // operator is a dimension of the mail: `from:sarah` suppresses the insured rows outright
    // (`hasInsuredTerm` below), so counting it here would record a book search that never ran. That
    // also covers the half-typed operator the offer pill puts in the box, which settles like any
    // other keystroke with nothing asked for yet. An operator search is counted where it lands —
    // `inbox-filter-provider`'s committed capture, under `["conversations", "from"]`.
    if (!parseSearchQuery(settledTerm).search || typedSearch?.term !== settledTerm) {
      return;
    }

    typedSearchRef.current = null;

    capture(PostHogEvent.SearchFilterUsed, {
      routeName: pathname,
      surface: SearchFilterSurface.Global,
      action: SearchFilterAction.Search,
      filterKeys: ["insureds"],
      durationMs: Date.now() - typedSearch.startedAt,
    });
  }, [settledTerm]);

  // The typed term split into the dimensions the server takes, on both clocks the box already runs:
  // the live term for the insured half, which is a local fuzzy pass, and the settled one for the
  // conversation queries, which are a scan.
  const typedQuery = parseSearchQuery(term);
  const settledQuery = parseSearchQuery(settledTerm);

  // The insured half matches the unscoped text only. `subject:` and `from:` are dimensions of the
  // mail and an insured name is neither, so the raw term would fuzzy-match "from:sarah" against the
  // book — and an operator-only term matches the *whole* book, which is why the bands below ask
  // whether there is an unscoped term at all rather than trusting the results.
  const hasInsuredTerm = Boolean(typedQuery.search);
  const { book: bookInsureds, other: otherInsureds, loading: insuredsLoading } = useSearchInsureds(typedQuery.search);

  const suggestionInput = {
    ...filters,
    // A committed search drops the label filter (see `inbox-filter-provider`), so the dropdown
    // drops it too — otherwise it hides hits that "View all results" would go on to show.
    labels: [],
    // The typed term and nothing else, which is the whole search Enter is about to run: `commit`
    // replaces every dimension, so a chip that is up but not retyped is about to be dropped and
    // previewing through it would show results the commit will not return. The three keys are
    // written even when undefined, because that is what overrides the committed values `filters`
    // spread in above.
    ...settledQuery,
    limit: CONVERSATION_QUERY_LIMIT,
    offset: 0,
    // 5-20x faster than the counted query, and the dropdown renders neither count nor pager.
    skipCount: true,
  };
  // Measured on the parsed query rather than the raw term, so the three characters are three
  // characters of *search text*: `subject:re` is a two-letter subject term and not worth a scan.
  // Zero characters covers the box sitting empty under a chip, which is the offers and the recent
  // searches rather than a preview of anything.
  const skipSuggestions = !isOpen || searchTextLength(settledQuery) < MINIMUM_SUGGESTION_TERM_LENGTH;

  // Subject and participants only. Both read `conversation` alone, so this comes back in roughly
  // the time the dropdown takes to open and gives the list something real to paint.
  const { data: headerSuggestions } = useInboxSearchSuggestionsQuery({
    variables: { input: { ...suggestionInput, skipBodySearch: true } },
    skip: skipSuggestions,
    fetchPolicy: "cache-and-network",
  });

  // The same search with the message-body tiers added. This is the half that reaches into `message`
  // and costs roughly a second even after the scan was scoped to the caller's inbox (B-1788), which
  // is exactly why it is asked for separately and rendered last.
  const { data: fullSuggestions } = useInboxSearchSuggestionsQuery({
    variables: { input: { ...suggestionInput, skipBodySearch: false } },
    skip: skipSuggestions,
    fetchPolicy: "cache-and-network",
  });

  // The two name searches in that same fast band see the unscoped half of the term and nothing
  // else, for the reason the insured band does: `subject:` and `from:` are dimensions of the mail,
  // and neither a person's name nor an agency's is one. That is also why the floor is measured
  // again here rather than left to `skipSuggestions`, which adds every dimension together —
  // `subject:renewal` clears three characters while carrying no name to match.
  const nameTerm = settledQuery.search;
  const skipNameSearches = skipSuggestions || nameTerm.length < MINIMUM_SUGGESTION_TERM_LENGTH;

  // Its own query because it answers with people rather than mail: `user_account` matched by name
  // or address, then one probe of the mailbox to keep only the agents this broker has corresponded
  // with (`agent-search.query.ts`).
  //
  // No `limit`, deliberately. `inbox-filter-provider`'s committed-search probe runs this same
  // operation with these same two variables, and matching it exactly is what makes that probe a
  // cache hit rather than a second request per committed search.
  const { data: agentSuggestions, error: agentSuggestionsError } = useInboxAgentSearchQuery({
    variables: { input: { inboxIds: filters.inboxIds, term: nameTerm } },
    skip: skipNameSearches,
    fetchPolicy: "cache-and-network",
  });

  // The same three moves one level up: `agency.name` supplies the candidates, and one multi-key
  // participant probe keeps the agencies this broker's mailboxes have actually written to
  // (`agency-search.query.ts`).
  //
  // `showExpired` comes off the Book rail's own setting because `accountCount` is a promise about
  // what picking the row opens onto. The rail drops plans effective more than 30 days ago unless it
  // is set, so a count taken without it describes a different book than the one the scope will show
  // — measured on the production copy, 449 of 1,136 (broker, agency) pairs held only
  // expired-but-open plans.
  const { data: agencySuggestions, error: agencySuggestionsError } = useInboxAgencySearchQuery({
    variables: { input: { inboxIds: filters.inboxIds, term: nameTerm, showExpired } },
    skip: skipNameSearches,
    fetchPolicy: "cache-and-network",
  });

  /**
   * Whether every query in the fast half of the search has answered.
   *
   * They are one latency band: all three read indexed columns, all three come back in roughly the
   * time the dropdown takes to open, and all three fire off the same settled term. Releasing them
   * together is what keeps the agent and agency bands from being an insertion *above* rows the
   * broker is already reading — the thing a band in the middle of the list can do that the body
   * band at the bottom cannot.
   */
  const fastSuggestionsSettled =
    Boolean(headerSuggestions) &&
    (skipNameSearches ||
      (hasAnswered(agentSuggestions, agentSuggestionsError) && hasAnswered(agencySuggestions, agencySuggestionsError)));

  const { agents, agencies, headerConversations, bodyConversations } = useMemo(() => {
    // A band still off screen cannot displace anything. This subsumes the rule the body band used to
    // carry alone: a body-only match is one the full search found and the header search did not, so
    // rendering the full query's rows without a header result — if it resolves first off a warm
    // cache — would put subject matches at the bottom under a body icon and then move them.
    if (!fastSuggestionsSettled) {
      return { agents: [], agencies: [], headerConversations: [], bodyConversations: [] };
    }

    const headers = headerSuggestions?.inboxConversations.conversations ?? [];
    // Built from the whole header window, not the rendered slice — a header hit past the slots is
    // still a header hit, and treating it as a body match would file a subject match at the bottom
    // of the list under a body icon.
    const headerIds = new Set(headers.map((conversation) => conversation.id));

    return {
      agents: (agentSuggestions?.inboxAgentSearch ?? []).slice(0, AGENT_SLOTS),
      agencies: (agencySuggestions?.inboxAgencySearch ?? []).slice(0, AGENCY_SLOTS),
      headerConversations: headers.slice(0, HEADER_CONVERSATION_SLOTS),
      bodyConversations: (fullSuggestions?.inboxConversations.conversations ?? [])
        .filter((conversation) => !headerIds.has(conversation.id))
        .slice(0, BODY_CONVERSATION_SLOTS),
    };
  }, [agencySuggestions, agentSuggestions, fastSuggestionsSettled, fullSuggestions, headerSuggestions]);

  const myInsureds = hasInsuredTerm ? bookInsureds.slice(0, MY_INSURED_SLOTS) : [];
  const otherBookInsureds = hasInsuredTerm ? otherInsureds.slice(0, OTHER_INSURED_SLOTS) : [];

  // The route chip scopes one of the two halves, so the copy moves by one word rather than
  // claiming the whole box narrowed: insured suggestions stay the whole book, which is how a broker
  // switches insureds from a scoped page. "their" is the chip, sitting immediately to the left of
  // this text — a pronoun, because that chip shows a *name* and something has to say what the name
  // is doing to the email half. It reads the same for either kind of scope, which is the point:
  // whether the name is an insured or an agent, the email half is theirs and the book half is not.
  //
  // The unlinked chip deliberately adds nothing here. It shows the concept itself, so a matching
  // word would just read it back: "✕ Unlinked | Search insureds and unlinked email". One condition
  // is the whole rule, which is why this is a template and not a composed list.
  const placeholderText = `Search insureds and ${clearScopeTarget ? "their " : ""}email`;
  const hasRecentSearches = recentSearches.length > 0;
  // The offer row carries its own emptiness check, unlike the dropdown around it: an empty padded
  // flex row still takes a gap, so it cannot just render with nothing in it. Same reason
  // `SearchScopeChips` returns null rather than an empty box. Every pill offers something the box
  // does not already have, so it takes every scope being on for the row to empty — history no
  // longer keeps it alive now that there is no clear-all pill.
  const hasOffers = !unlinked || !subjectSearch || !senderSearch;
  const hasSuggestions =
    myInsureds.length > 0 ||
    agents.length > 0 ||
    agencies.length > 0 ||
    headerConversations.length > 0 ||
    otherBookInsureds.length > 0 ||
    bodyConversations.length > 0;

  // "Nothing matched" is only true once everything that could match has answered. The body half
  // alone costs about a second, so a message gated on anything less would flash over a search still
  // running — the same failure the band order exists to avoid. The agent and agency queries ride in
  // through `fastSuggestionsSettled`, and both have to: every async source the list draws from
  // belongs in this gate, because one left out of it is one the message is free to lie about — and
  // "one" is the newest one as easily as the oldest, so a band added later goes in here too rather
  // than beside it. `skipSuggestions` is in there on its own account — Apollo clears `data` while a
  // query is skipped today but says it intends to stop doing that, and without this term a term
  // shortened back below the minimum would answer with the previous term's empty result.
  const hasSearched = !skipSuggestions && fastSuggestionsSettled && Boolean(fullSuggestions) && !insuredsLoading;
  const foundNothing = hasSearched && !hasSuggestions;
  const horizonDescription = useMailHorizonDescription({ skip: !foundNothing });

  /**
   * The one place the unlinked scope is written, so the offer, the chip and Backspace cannot drift
   * on either the URL or the event. The capture is the header checkbox's verbatim — same event,
   * same `surface`, same `filterKeys` — because the four weeks of zeroes that control recorded are
   * the baseline this relocation is measured against, and a renamed event loses the comparison.
   * Only `?unlinked=` moves, so the committed `?term=` rides through: removal widens rather than
   * resets, the same as the insured chip.
   */
  const setUnlinkedScope = (nextUnlinked: boolean) => {
    capture(PostHogEvent.SearchFilterUsed, {
      routeName: pathname,
      surface: SearchFilterSurface.Unknown,
      action: nextUnlinked ? SearchFilterAction.FilterAdded : SearchFilterAction.FilterRemoved,
      filterKeys: ["unlinked"],
    });
    setUnlinked(nextUnlinked);
  };

  // The highlight guard resets with the dropdown: it reopens with cmdk's first row highlighted
  // again, so the next Enter has to go back to committing.
  const dismiss = () => {
    setIsOpen(false);
    hasMovedHighlightRef.current = false;
    inputRef.current?.blur();
  };

  // A commit keeps the scope the box is showing, so it goes to `rootScopePath` from every
  // section. With no chip that value is `rootPath`, which is how an unscoped commit still lands on
  // the whole mailbox. Removing the chip is the way to widen a committed search; the commit itself
  // does not do it silently. The book results beside the mail list stay agency-wide under an
  // insured chip, which is the split the dropdown already makes — insured suggestions global, email
  // scoped; an agent or agency chip also fences the rail, because each names a *set* of accounts
  // rather than one (see `useScopedItems`). The rest of the query string rides along rather than
  // being rewritten, because `?labels=`, `?unlinked=` and `?drafts=` are the broker's own state and
  // a bare `?term=` would close an open composer. It pushes, so Back returns the thread this drops.
  //
  // Takes the term rather than reading state, because a recent row commits a term that is not in
  // the box: `setTerm(x)` has not landed by the time the handler runs, so a commit that read state
  // would search whatever was there before. Required rather than defaulted so every call site says
  // which term it means.
  const commit = (committedTerm: string) => {
    // The operators come out of the term here rather than at the far end, which is what makes them
    // chips: `?term=` keeps the free text the broker can still read in the field, and each operator
    // lands in a param of its own that the chip renders from.
    const committedQuery = parseSearchQuery(committedTerm);

    if (searchTextLength(committedQuery) === 0) {
      // Nothing to search for. Three ways to land here and only one of them is a clear, so the test
      // is what the broker did to the field rather than what the parse came back with.
      //
      // Erasing a committed term and pressing Enter is the clear, and it clears all of it — chips
      // included, the same as the ✕ and Escape, because those are three doors onto one intent and
      // cannot mean different amounts of it. It clears in place: navigating would make erasing a
      // text box a page move, and there are no results to land on, so a collapsed book stays
      // collapsed.
      //
      // The other two only look like it. A bare `subject:` is the prefix the offer pill leaves
      // behind before a value is typed after it — it asks for nothing, so it takes nothing, or
      // reaching for a second operator would throw the first one away. And an already-empty field
      // is the resting state of a chip-only search, not an erasure: after `?subject=renewal` the
      // field is empty *by design*, so a bare Enter there has to be a no-op rather than a way to
      // lose the search by tabbing through the box. `searchTerm` is what tells them apart — it is
      // the committed free text the field was seeded with, so it is non-empty exactly when there
      // was something in the field to erase.
      if (searchTerm && !committedTerm.trim()) {
        clearSearch();
      }
      dismiss();
      return;
    }

    // The raw term, operators and all, because that is what re-running it has to reproduce — the
    // recent row commits straight back through here.
    recordRecentSearch(committedTerm);

    const nextParams = new URLSearchParams(locationSearch);
    // A commit writes the whole search, all three dimensions, replacing whatever was there. The
    // alternative was letting a dimension the term did not name ride along the way `?labels=` and
    // `?unlinked=` do, on the reasoning that removing the chip is how you widen — but those two are
    // controls the broker sets and leaves set, and this is a search box. Typing `from:kyle` after a
    // `subject:` search reads as asking for a new search, and answering it with the old subject
    // still on is the silent narrowing this feature exists to stop. Gmail replaces here too.
    //
    // Combining dimensions is still one term — `subject:renewal from:kyle` — and the chips are
    // still how you drop one without retyping the rest. An emptied param goes rather than
    // lingering: a bare `?term=` would hold the book on a results tab with nothing to put there.
    const committedDimensions: [param: string, value: string | undefined][] = [
      ["term", committedQuery.search],
      ["subject", committedQuery.subjectSearch],
      ["from", committedQuery.senderSearch],
    ];

    for (const [param, value] of committedDimensions) {
      if (value) {
        nextParams.set(param, value);
      } else {
        nextParams.delete(param);
      }
    }
    // The open thread would sit over the results the commit just asked for, and a new search starts
    // at the first page.
    nextParams.delete("conversationId");
    nextParams.delete("page");

    // The field keeps the free text and nothing else, because the operators are chips now. Said
    // here rather than left to the `searchTerm` sync, which cannot see this: `?term=` is *deleted*
    // for an operator-only search, so a box waiting on that value would sit through `"" -> ""` and
    // go on showing the raw `subject:nashville` alongside the chip it just became.
    setTerm(committedQuery.search);

    // The results land on the book's results tab, which is worth nothing behind a collapsed book
    // view. This is where the old in-book search box expanded it too.
    setIsBookViewExpanded(true);
    navigate({ pathname: rootScopePath, search: `?${nextParams.toString()}` });
    dismiss();
  };

  const handleTermChange = (nextTerm: string) => {
    typedSearchRef.current = { term: nextTerm, startedAt: Date.now() };
    hasMovedHighlightRef.current = false;
    setTerm(nextTerm);
    setIsOpen(true);
  };

  // The dropdown's lifetime follows focus being *somewhere in the box*, not focus being in the
  // field, which is why this sits on the container and not on the input. Tab out of the field lands
  // on the offers row and Tab again leaves entirely; only the second one is a dismissal. A click
  // never reaches this at all: `SearchDropdown` preventDefaults its `onMouseDown`, so the caret does
  // not leave the field.
  const handleBlur = (event: React.FocusEvent<HTMLDivElement>) => {
    if (containerRef.current?.contains(event.relatedTarget)) {
      return;
    }

    dismiss();
  };

  /**
   * Every control in the dropdown ends by putting the broker back in the box.
   *
   * A click never lost the caret — `SearchDropdown` and the controls preventDefault their
   * `onMouseDown` — so this is for the keyboard, where Tab genuinely moved focus onto the control.
   * A row's remove control needs it more than the pills do, because activating it *unmounts it*:
   * the row goes with its term. Focus then falls to `body`, `handleBlur` sees a `relatedTarget`
   * outside the box, and the whole dropdown dismisses — so a broker removing one term of three
   * would lose the other two off the screen.
   *
   * Returning focus synchronously is what avoids that rather than papering over it: React has not
   * re-rendered yet, so the caret lands in the input while the control is still mounted, and the
   * blur that follows carries a `relatedTarget` the container contains.
   */
  const handleAddUnlinkedScope = () => {
    setUnlinkedScope(true);
    inputRef.current?.focus();
  };

  /**
   * Where an operator pill leads: the syntax itself, typed into the box for the broker. It cannot
   * turn a scope on the way the unlinked pill does, because an operator with no value narrows
   * nothing — so it hands over the prefix and lets them finish it, which is also where they see the
   * syntax they can type for themselves next time.
   *
   * Through `handleTermChange` rather than `setTerm`, so this counts as the start of a typing
   * session exactly as the first keystroke would. `parseSearchQuery` holds the other half: a
   * dangling prefix asks for nothing, so the box waits for a value without firing a query.
   */
  const handleAddOperator = (operator: SearchOperator) => {
    handleTermChange(`${operator}:`);
    inputRef.current?.focus();
  };

  const handleRemoveRecentSearch = (recentTerm: string) => {
    removeRecentSearch(recentTerm);
    inputRef.current?.focus();
  };

  /**
   * The two ways a broker walks away from what they were typing: erasing it, and picking a row
   * instead of finishing the search. Both replace the term from outside the keyboard, so both end
   * the typing session and drop its stamp.
   *
   * Comparing the settled term against the typed term is not enough by itself here, because the two
   * can be the same string — type "acme", pick an insured before the debounce settles, then run
   * "acme" again off the history. That is not a corner case for this feature, it is the feature: the
   * whole point of the band is re-running a term you typed before. Without this the second run would
   * spend the abandoned stamp on a phantom "insureds" search carrying the first run's start time.
   *
   * Deliberately not done in the `searchTerm` sync effect. That would also silence the capture for a
   * broker who types and commits inside the debounce window, which is a real typed search and fires
   * today — a change to what the existing event counts, which is not this PR's to make.
   */
  const abandonTypedSearch = (nextTerm: string) => {
    typedSearchRef.current = null;
    setTerm(nextTerm);
  };

  // Clearing takes the operator chips with the term. They were typed into this box like the rest of
  // the term and they read as part of it, so leaving them standing over an emptied field is a
  // half-cleared search — and the next term the broker types silently inherits the scope they
  // thought they had just thrown away. The scopes set from outside the box stay: `?unlinked=` has
  // its own control and the insured chip is the route.
  const handleClear = () => {
    abandonTypedSearch("");
    clearSearch();
    dismiss();
  };

  // Picking a row is not committing a search: the page keeps whatever `?term=` it already had, so
  // the box drops the text the broker typed to find the row and goes back to showing that. Without
  // this the box keeps stale text the rest of the page is not filtered by, and the next keystroke
  // lands on the end of it.
  const dismissToCommittedTerm = () => {
    abandonTypedSearch(searchTerm);
    dismiss();
  };

  // Both picks record before `dismissToCommittedTerm` runs, because that is the call that loses the
  // term: it puts the box back on the committed `?term=`, which is empty for the broker who typed,
  // read the dropdown and clicked a row without ever pressing Enter. That is the common path, and
  // until now the typed term did not survive it.
  const handleSelectInsured = (selectedInsuredId: string) => {
    recordRecentSearch(term);
    dismissToCommittedTerm();
    navigate(`${insuredRootPath}/${selectedInsuredId}`);
  };

  /**
   * Picking an agent or an agency is a scope change rather than an open. There is no page for
   * either to land on: the route narrows the mail list and fences the Book rail (B-1844, B-1934).
   * `scopeSegment` composes the path the chip's ✕ takes back off again, so the way in and the way
   * out cannot disagree about where the scope lives.
   *
   * One handler for both kinds, because the pick is the same move at two widths and `InboxScope`
   * already types them as siblings — the insured kind is the one excluded, since that pick opens a
   * page rather than narrowing one (`handleSelectInsured`).
   *
   * `rootPath` rather than `insuredRootPath`, from every section. Both routes are declared under
   * `/inbox` alone, so a pick from drafts or standalone chat travels there the same way an email
   * pick does — there is nothing in those sections for the scope to narrow.
   */
  const handleSelectScope = (scope: Exclude<InboxScope, { kind: "insured" }>) => {
    recordRecentSearch(term);
    dismissToCommittedTerm();
    navigate(`${rootPath}/${scopeSegment(scope)}`);
  };

  const handleSelectConversation = (selectedConversationId: string) => {
    recordRecentSearch(term);
    dismissToCommittedTerm();

    if (ownsSearchSurfaces) {
      setConversationId(selectedConversationId);
      return;
    }

    // Drafts and standalone chat render no conversation column, so writing the query state there
    // would open nothing. The pick hops to the inbox instead, the way the Alby menu's rows do.
    navigate({ pathname: rootScopePath, search: `?conversationId=${selectedConversationId}` });
  };

  /**
   * Running a term the broker already used. The capture is the only signal this band will ever
   * produce: the term itself is deliberately unrecordable — `posthog-tracker.tsx` keeps search text
   * out of analytics on purpose — so "did anyone touch these rows" has to be answerable without it.
   * `filterKeys` carries the band, the way `unlinked` and `insureds` already do, rather than a new
   * event name; the taxonomy lives in a published package, so a new name is not a local change.
   *
   * Note for anyone counting searches later: this fires *alongside* the `["conversations"]` capture
   * `inbox-filter-provider` emits when the commit resolves. That is correct — it is a committed
   * search — but the committed-search baseline has to keep keying on `["conversations"]`.
   */
  const handleSelectRecentSearch = (recentTerm: string) => {
    capture(PostHogEvent.SearchFilterUsed, {
      routeName: pathname,
      surface: SearchFilterSurface.Global,
      action: SearchFilterAction.Search,
      filterKeys: ["recent"],
    });
    commit(recentTerm);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Escape") {
      event.preventDefault();
      handleClear();
      return;
    }

    // The chips read as the tokens before the caret, so an emptied input backspaces into them one
    // at a time. Nearest first, which is what `SearchScopeChips` renders last: the operators sit
    // against the caret — they are text the broker typed there — then the unlinked scope, then the
    // route scope furthest away, so a broker deletes what they can see themselves deleting. Every
    // one of them widens rather than clears: all four paths carry the committed `?term=` through.
    if (event.key === "Backspace" && !term) {
      if (senderSearch) {
        event.preventDefault();
        setSenderSearch("");
        return;
      }

      if (subjectSearch) {
        event.preventDefault();
        setSubjectSearch("");
        return;
      }

      if (unlinked) {
        event.preventDefault();
        setUnlinkedScope(false);
        return;
      }

      if (clearScopeTarget) {
        event.preventDefault();
        navigate(clearScopeTarget.to);
        return;
      }
    }

    if (event.key === "ArrowDown" || event.key === "ArrowUp" || event.key === "Home" || event.key === "End") {
      hasMovedHighlightRef.current = true;
      return;
    }

    if (event.key === "Enter" && !hasMovedHighlightRef.current) {
      // Stops cmdk's root handler from firing the highlighted item instead.
      event.preventDefault();
      commit(term);
    }
  };

  return (
    <Command
      ref={containerRef}
      onBlur={handleBlur}
      shouldFilter={false}
      loop
      className="text-foreground relative h-8 min-w-0 flex-1 overflow-visible bg-transparent sm:basis-96"
    >
      <CommandInput
        ref={inputRef}
        value={term}
        onValueChange={handleTermChange}
        onKeyDown={handleKeyDown}
        onFocus={() => setIsOpen(true)}
        placeholder={placeholderText}
        aria-label={placeholderText}
        leading={
          <SearchScopeChips
            onRemoveUnlinked={() => setUnlinkedScope(false)}
            onRemoveSubject={() => setSubjectSearch("")}
            onRemoveSender={() => setSenderSearch("")}
          />
        }
        // `min-w-0` is what lets the chips share the row: an input's automatic minimum size is its
        // default character count, so without it the field would overflow the pills rather than shrink.
        className="bg-foreground/7.5 h-8 gap-2 rounded-full pr-8 [&_[cmdk-input]]:h-8 [&_[cmdk-input]]:min-w-0 [&_[cmdk-input]]:text-sm"
      />
      {/* On the chips as well as the term, because a committed operator search leaves the field
          empty by design — gating on `term` alone would take the way out away at exactly the moment
          the box is at its most narrowed. The per-chip ✕ removes one dimension; this one is how the
          whole search goes. */}
      {(term || subjectSearch || senderSearch) && (
        <button
          type="button"
          aria-label="Clear search"
          className="absolute inset-y-0 right-2 my-auto flex size-4 cursor-pointer opacity-50"
          onClick={handleClear}
        >
          <Icon icon="cancel" className="filled my-auto text-lg" />
        </button>
      )}
      {/* The dropdown does two jobs and they belong to opposite states of the box: empty, it says
          where the box has already been and what it can be narrowed *to*, and with a term it says
          what matched. Two branches on the term rather than one guard with the halves gated inside
          it, so the exclusivity is the structure. Neither renders on nothing — an empty popover
          under a focused field is a hole — which is why the empty branch now asks about both of its
          halves: recents alone are reason enough to open it, and so is the offer alone.

          The two halves are gated separately because they empty for unrelated reasons. The offers
          have nothing to say once every scope they turn on is already on; the recents have nothing
          to say until the broker has searched. Wrapping both in the scope offer's condition — which
          is what this branch used to be — would hide a broker's history behind a checkbox they
          happened to tick.

          Offers on top, then the terms. The separator carries both halves' conditions because it is
          a line *between* them: without the terms it underlines nothing, and with every scope
          already on there is nothing above it to divide and it would lead the list. It used to need
          only `hasRecentSearches`, which was safe when a clear-all pill rode on that same condition.

          Both are inbox-only, which is the reach the header checkbox had: `unlinked` scopes the mail
          list, and the inbox is the only section that renders one. Offering it from drafts would
          also hand back a scope that cannot survive the next click — a pick there navigates with a
          rewritten query string (`handleSelectConversation`), so the param would be gone the moment
          it was used. Keeping the one entry point where the control already lived is also what keeps
          the four weeks of zeroes a like-for-like baseline. Recents share that gate for a plainer
          reason: a commit from drafts lands in the inbox, so history offered there sends the broker
          somewhere else to read it. The *chip* is deliberately not gated: `?unlinked=true` is
          bookmarkable, and a narrowed box has to say so wherever it loads. */}
      {isOpen && !term && ownsSearchSurfaces && (
        <SearchDropdown>
          {hasOffers && (
            <SearchOfferRow>
              {!unlinked && <UnlinkedScopeOffer onAdd={handleAddUnlinkedScope} />}
              {!subjectSearch && <OperatorOffer operator="subject" onAdd={handleAddOperator} />}
              {!senderSearch && <OperatorOffer operator="from" onAdd={handleAddOperator} />}
            </SearchOfferRow>
          )}
          {/* The line owns both of its gaps, because neither neighbour contributes one: the offer
              row has no padding of its own, and below the line the first term is the row cmdk
              highlights by default — `bg-accent` fills that row's padding, so the gap that exists in
              its box model is not a gap on screen. 6px each side is therefore 6px each side. */}
          {hasOffers && hasRecentSearches && <CommandSeparator className="my-1.5" />}
          {recentSearches.map((recentTerm) => (
            <RecentSearchItem
              key={recentTerm}
              term={recentTerm}
              onSelect={handleSelectRecentSearch}
              onRemove={handleRemoveRecentSearch}
            />
          ))}
        </SearchDropdown>
      )}
      {isOpen && term && (hasSuggestions || horizonDescription !== null) && (
        <SearchDropdown>
          {myInsureds.map((insured) => (
            <InsuredSuggestion key={insured.id} insured={insured} onSelect={handleSelectInsured} />
          ))}
          {agents.map((agent) => (
            <AgentSuggestion
              key={agent.id}
              agent={agent}
              onSelect={(agentId) => handleSelectScope({ kind: "agent", agentId })}
            />
          ))}
          {agencies.map((agency) => (
            <AgencySuggestion
              key={agency.id}
              agency={agency}
              onSelect={(agencyId) => handleSelectScope({ kind: "agency", agencyId })}
            />
          ))}
          {headerConversations.map((conversation) => (
            <ConversationSuggestion
              key={conversation.id}
              conversation={conversation}
              currentUserEmail={userInbox?.email}
              onSelect={handleSelectConversation}
            />
          ))}
          {otherBookInsureds.map((insured) => (
            <InsuredSuggestion key={insured.id} insured={insured} onSelect={handleSelectInsured} />
          ))}
          {bodyConversations.map((conversation) => (
            <ConversationSuggestion
              key={conversation.id}
              conversation={conversation}
              currentUserEmail={userInbox?.email}
              onSelect={handleSelectConversation}
            />
          ))}
          {horizonDescription && (
            // Not a `CommandItem`: there is nothing here to select. It sits above the commit
            // because it answers "why is this empty" before offering the one action left.
            <p className="text-muted-foreground px-2 py-1.5 text-xs">{horizonDescription}</p>
          )}
          {/* Wrapped rather than passed straight through: cmdk hands `onSelect` the item's `value`,
              which `commit` would read as the term to search. */}
          <CommandItem value="view-all-results" onSelect={() => commit(term)}>
            <Icon icon="search" className="text-sm" />
            <span className="truncate">View all results for “{term}”</span>
          </CommandItem>
        </SearchDropdown>
      )}
    </Command>
  );
};
